import React, { useState, useEffect } from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../store/store';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { Cancel as CancelIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { api } from '../../../../services/api';
import TripInformationSection from './tripInformation/TripInformationSection';
import TripFeedbackSection from './TripFeedbackSection';
import TripCommentsSection from './TripCommentsSection';
import TripPricingSection from './TripPricingSection';
import TripExpensesSection from './TripExpensesSection';
import CustomerDetailsSection from './CustomerDetailsSection';
import ContactRequestPanel from '../contactRequest/ContactRequestPanel';
import TripExtraChargesSection from './TripExtraChargesSection';
import TripDriverSection from './TripDriverSection';
import TripCarSection from './TripCarSection';
import TripPassengersSection from './TripPassengersSection';
import TripAddressesSection from './TripAddressesSection';
import TripStatusSection from './TripStatusSection';

// Import styled components
import {
  DetailSection,
  DetailContainer
} from '../../TripDetailPanel.styles';
import CancelTripDialog from './CancelTripDialogue';

interface TripDetailPanelProps {
  tripCode?: string;
  tripDetail?: any;
}

interface TripDetails {
  tripCode: string;
  updatedAt:string;
  tripStatus: string;
  sourceCity: string;
  destinationCities: string;
  startDatetime: string;
  startOdometer?: number | null;
  endOdometer?: number | null;
  pickUpOdometer?: number | null;
  dropOffOdometer?: number | null;
  linkedTripCode?: string;
  zohoInvoiceId?: string;
  endDatetime?: string;
  pickupAddress?: string;
  dropAddress?: string;
  notes?: string;
  travelServiceDetailId?: number;
  passengerName?: string;
  contactNumber?: string;
  email?: string;
  pickupInfo?: string;
  dropInfo?: string;
  specialRequirements?: string;
  driverId?: number;
  carId?: number;
  driver?: {
    id: number;
    firstName?: string;
    lastName?: string;
    mobileNumber?: string;
    email?: string;
    role?: string;
    licenseNumber?: string;
    rating?: number;
  };
  request?: string;
  vendorGst?: string;
  addresses?: {
    id?: number;
    addressType: 'PICKUP' | 'DROP';
    address: string;
    city: string;
    state: string;
    postalCode: string;
    landmark?: string;
    latitude?: number;
    longitude?: number;
  }[];
  travelServiceDetail?: {
    id?: number;
    carType?: {
      id?: number;
      carType?: string;
      makeCompany?: string;
      model?: string;
      capacity?: string;
      fuelType?: string;
      engineCapacity?: string;
      transmission?: string;
      features?: string[];
    };
    displayPrice?: number;
    offerPrice?: number;
    negotiationPrice?: number;
    ratePerExtraKm?: number;
    slot?: string;
    travelService?: {
      id?: number;
      name: string;
      serviceName?: string;
      serviceType?: string;
    };
  };
  travelService?: {
    serviceName?: string;
    serviceType?: string;
  };
  customer?: {
    id?: number;
    name: string;
    email?: string;
    mobileNumber?: string;
    address?: string;
    emergencyContact?: string;
    emergencyContactName?: string;
  };
  feedback?: any;
  comments?: any[];
  pricing?: any;
  expenses?: any[];
  extraCharges?: any[]; // Add extraCharges field
  passengers?: any[]; // Add passengers field
  car?: {
    id: number;
    plateNumber: string;
    manufacturedYear: string;
    fastTagId: string;
    photoId: string;
    carType: string;
  };
  payments?: {
    id: number;
    transactionType: string;
    amount: number;
    paymentMethod: string;
    paymentStatus: string;
    transactionReference: string;
    transactionDate: string;
    razorpayPaymentLinkUrl?: string | null;
    razorpayPaymentLinkId?: string | null;
    cashInHand?: string;
  }[];
}

interface TripUpdatePayload {
  customerId: number;
  travelServiceDetailId: number;
  sourceCity: string;
  destinationCities: string;
  startDatetime: string;
  endDatetime: string;
  startOdometer?: number | null;
  endOdometer?: number | null;
  pickUpOdometer?: number | null;
  dropOffOdometer?: number | null;
  linkedTripCode?: string;
  request: string;
  notes: string;
  vendorGst?: string;
  pricing: {
    ratePerDay: number;
    ratePerExtraKm: number;
    ratePerExtraHour: number;
    driverAllowancePerDay: number;
    estimatedTripFare: number;
    discount: number;
    noOfDays: number;
    agreedTripFare: number;
  };
}

const TripDetailPanel: React.FC<TripDetailPanelProps> = ({ tripCode, tripDetail }) => {
  const [trip, setTrip] = useState<TripDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonOther, setCancelReasonOther] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelFormTouched, setCancelFormTouched] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const [amountDue, setAmountDue] = useState<number>(0);
  const [inCancellationConfirmation, setInCancellationConfirmation] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  
  const userRole = useSelector((state: any) => state.auth.user?.role);
  const isAdmin = userRole === 'ADMIN';
  const isAccountant = userRole === 'ACCOUNTANT';

  // Predefined cancellation reasons
  const cancellationReasons = [
    'Customer requested cancellation',
    'Driver unavailable',
    'Vehicle breakdown',
    'Weather conditions',
    'Booking error',
    'Duplicate booking',
    'Price dispute',
    'Other'
  ];  
  // Fetch trip details based on trip code
  const fetchTripDetails = async (code: string) => {
    try {
      
      setLoading(true);
      setError(null);
      // Fetch main trip details
      const response = await api.getTripDetails(code) as any;
      
      if (response && response.data) {
        const apiData = response.data;
        // Use data directly from main response instead of separate API calls

        const tripData = {
          tripCode: apiData.tripCode || code,
          updatedAt:apiData.updatedAt,
          tripStatus: apiData.tripStatus || 'UNKNOWN',
          sourceCity: apiData.sourceCity || 'N/A',
          destinationCities: apiData.destinationCities || 'N/A',
          startDatetime: apiData.startDatetime || '',
          endDatetime: apiData.endDatetime || null,
          startOdometer: apiData.startOdometer || null,
          endOdometer: apiData.endOdometer || null,
          pickUpOdometer: apiData.pickUpOdometer || null,
          dropOffOdometer: apiData.dropOffOdometer || null,
          linkedTripCode: apiData.linkedTripCode || '',
          zohoInvoiceId: apiData.zohoInvoiceId || '',
          pickupAddress: apiData.pickupAddress || '',
          dropAddress: apiData.dropAddress || '',
          notes: apiData.notes || '',
          travelServiceDetailId: apiData.travelServiceDetailId,
          travelServiceDetail: apiData.travelServiceDetail,
          vendorGst: apiData.vendorGst || '',

          // Add passenger information fields
          passengerName: apiData.passengerName || apiData.passengers?.[0]?.passengerName || '',
          contactNumber: apiData.contactNumber || apiData.passengers?.[0]?.contactNumber || '',
          email: apiData.email || apiData.passengers?.[0]?.email || '',
          pickupInfo: apiData.pickupInfo || apiData.passengers?.[0]?.pickupInfo || '',
          dropInfo: apiData.dropInfo || apiData.passengers?.[0]?.dropInfo || '',
          specialRequirements: apiData.specialRequirements || apiData.passengers?.[0]?.specialRequirements || '',

          // Add trip metadata
          driverId: apiData.driverId || apiData.driver?.id || null,
          carId: apiData.carId || apiData.car?.id || null,
          driver: apiData.driver || null,
          request: apiData.request || '',

          // Map addresses data
          addresses: apiData.addresses ? apiData.addresses.map((address: any) => ({
            id: address.id,
            addressType: address.addressType || 'PICKUP',
            address: address.address || address.addressLine1 || '',
            city: address.city || '',
            state: address.state || '',
            postalCode: address.postalCode || address.pincode || '',
            location: address.location || '',
            landmark: address.landmark || '',
            latitude: address.latitude,
            longitude: address.longitude
          })) : [],

          // Map customer data from main response - use all available customer fields
          customer: apiData.customer ? {
            id: apiData.customer.id || apiData.customer.customerId,
            name: apiData.customer.name || apiData.customer.customerName || apiData.customer.fullName || 'N/A',
            email: apiData.customer.email || apiData.customer.emailId || apiData.customer.emailAddress || '',
            mobileNumber: apiData.customer.mobileNumber || apiData.customer.mobile || apiData.customer.phone || apiData.customer.contactNumber || '',
            address: apiData.customer.address || apiData.customer.customerAddress || apiData.customer.fullAddress || '',
            emergencyContact: apiData.customer.emergencyContact || apiData.customer.emergencyContactNumber || apiData.customer.emergencyPhone || '',
            emergencyContactName: apiData.customer.emergencyContactName || apiData.customer.emergencyContactPerson || apiData.customer.emergencyContactPersonName || ''
          } : (apiData.customerName || apiData.customerMobile) ? {
            // Fallback mapping if customer object is not present but customer fields exist at root level
            id: null,
            name: apiData.customerName || 'N/A',
            email: apiData.customerEmail || '',
            mobileNumber: apiData.customerMobile || apiData.customerPhone || '',
            address: apiData.customerAddress || '',
            emergencyContact: apiData.emergencyContact || '',
            emergencyContactName: apiData.emergencyContactName || ''
          } : null,
          // Map feedback data from main response
          feedback: (() => {    
            return apiData.feedback ?
            Array.isArray(apiData.feedback)
              ? apiData.feedback.map((feedback: any) => ({
                id: feedback.id,
                rating: feedback.rating || 0,
                comments: feedback.comments || '',
                feedbackDate: feedback.feedbackDate || new Date().toISOString(),
                customerName: feedback.customerName || apiData.customer?.name || 'N/A' // <-- FIXED
              }))
              : [{
                id: apiData.feedback.id,
                rating: apiData.feedback.rating || 0,
                comments: apiData.feedback.comments || '',
                feedbackDate: apiData.feedback.feedbackDate || new Date().toISOString(),
                customerName: apiData.feedback.customerName || apiData.customer?.name || 'N/A' // <-- FIXED
              }]
            : [];
          })(),

          // Map comments data from main response
          comments: apiData.comments ?
            apiData.comments.map((comment: any) => ({
              id: comment.id,
              comment: comment.comment || comment.text || '',
              createdAt: comment.createdAt || new Date().toISOString(),
              createdByName: comment.createdByName, // <-- changed to always show "Operator"
              // authorType: comment.authorType || 'OPERATOR'
            })) : [],

          // Map pricing data from main response
          pricing: apiData.pricing ? {
            id: apiData.pricing.id,
            pricing: {
              baseFare: apiData.pricing.baseFare || 0,
              distanceCharges: apiData.pricing.distanceCharges || 0,
              timeCharges: apiData.pricing.timeCharges || 0,
              tolls: apiData.pricing.tolls || 0,
              parking: apiData.pricing.parking || 0,
              extraCharges: apiData.pricing.extraCharges || 0,
              taxes: apiData.pricing.taxes || 0,
              discount: apiData.pricing.discount || 0,
              totalAmount: apiData.pricing.totalAmount || 0,
              agreedTripFare: apiData.pricing.agreedTripFare || 0,
              // Add pricing details from travelServiceDetail
              displayPrice: apiData.travelServiceDetail?.displayPrice || 0,
              offerPrice: apiData.travelServiceDetail?.offerPrice || 0,
              negotiationPrice: apiData.travelServiceDetail?.negotiationPrice || 0,
              ratePerExtraKm: apiData.travelServiceDetail?.ratePerExtraKm || 0,
              basePricePerDay: apiData.travelServiceDetail?.basePricePerDay || 0
            },
            currency: apiData.pricing.currency || 'INR',
            paymentStatus: apiData.pricing.paymentStatus || 'PENDING',
            paidAmount: apiData.pricing.paidAmount || 0,
            pendingAmount: apiData.pricing.pendingAmount || 0
          } : (apiData.travelServiceDetail?.displayPrice || apiData.travelServiceDetail?.offerPrice || apiData.travelServiceDetail?.negotiationPrice || apiData.travelServiceDetail?.ratePerExtraKm || apiData.travelServiceDetail?.basePricePerDay) ? {
            // Create pricing object from travelServiceDetail if main pricing doesn't exist
            id: null,
            pricing: {
              baseFare: 0,
              distanceCharges: 0,
              timeCharges: 0,
              tolls: 0,
              parking: 0,
              extraCharges: 0,
              taxes: 0,
              discount: 0,
              totalAmount: apiData.travelServiceDetail?.offerPrice || apiData.travelServiceDetail?.displayPrice || 0,
              agreedTripFare: 0,
              displayPrice: apiData.travelServiceDetail?.displayPrice || 0,
              offerPrice: apiData.travelServiceDetail?.offerPrice || 0,
              negotiationPrice: apiData.travelServiceDetail?.negotiationPrice || 0,
              ratePerExtraKm: apiData.travelServiceDetail?.ratePerExtraKm || 0,
              basePricePerDay: apiData.travelServiceDetail?.basePricePerDay || 0
            },
            currency: 'INR',
            paymentStatus: 'PENDING',
            paidAmount: 0,
            pendingAmount: apiData.travelServiceDetail?.offerPrice || apiData.travelServiceDetail?.displayPrice || 0
          } : null,
          // Map expenses data from main response
          expenses: apiData.expenses ? apiData.expenses.map((expense: any) => ({
            id: expense.id,
            description: expense.place || `${expense.expenseType || 'Expense'}${expense.fuelQty ? ` - ${expense.fuelQty}L` : ''}`,
            amount: expense.amount || 0,
            category: expense.expenseType || 'OTHER',
            date: expense.datetime ? new Date(expense.datetime).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
            notes: expense.comment || '',
            // Keep original fields for additional info
            expenseType: expense.expenseType,
            datetime: expense.datetime,
            comment: expense.comment,
            place: expense.place,
            fuelQty: expense.fuelQty,
            odometerReading: expense.odometerReading
          })) : [],

          // Map extra charges data from main response
          extraCharges: apiData.extraCharges ? apiData.extraCharges.map((charge: any) => ({
            id: charge.id,
            chargeType: charge.label || charge.chargeType || 'OTHER', // API returns 'label', map to 'chargeType'
            label: charge.label, // Keep original label field
            amount: charge.amount || 0,
            description: charge.description || charge.label || 'Extra Charge',
            date: charge.date || charge.createdAt || new Date().toISOString().slice(0, 10),
            notes: charge.notes || charge.comment || ''
          })) : [],

          // Map passengers data from main response
          passengers: apiData.passengers ? apiData.passengers.map((passenger: any) => ({
            id: passenger.id,
            passengerName: passenger.passengerName || '',
            contactNumber: passenger.contactNumber || '',
            email: passenger.email || '',
            pickupInfo: passenger.pickupInfo || '',
            dropInfo: passenger.dropInfo || '',
            specialRequirements: passenger.specialRequirements || '',
            tripForSelf: passenger.tripForSelf || false
          })) : [],

          // Map car data from main response
          car: apiData.car ? {
            id: apiData.car.id,
            plateNumber: apiData.car.plateNumber,
            manufacturedYear: apiData.car.manufacturedYear,
            fastTagId: apiData.car.fastTagId,
            photoId: apiData.car.photoId,
            carType: apiData.car.carType
          } : undefined,
          payments: apiData.payments ? apiData.payments.map((payment: any) => ({
            id: payment.id,
            transactionType: payment.transactionType,
            paymentStatus: payment.paymentStatus,
            paymentMethod: payment.paymentMethod,
            transactionReference: payment.transactionReference,
            transactionDate: payment.transactionDate,
            razorPayPaymentLinkUrl: payment.razorpayPaymentLinkUrl || null,
            razorPaymentLinkId: payment.razorpayPaymentLinkId || null,
            amount: payment.amount,
            date: payment.date,
            cashInHand: payment.cashInHand || null,
          })) : [], 
        };    
        setTrip(tripData as TripDetails);
        
      } else {
        setError('No trip details found');
      }
    } catch (err: any) {
      console.error('Error fetching trip details:', err);
      setError(err.message || 'Failed to load trip details');
    } finally {
      setLoading(false);
    }
  }; 

  // this callback is for getting total paid and amount due from TripPricingSection and send to tripStatusSection
const handlePaymentCalculated = (calculatedTotalPaid:number, calculatedAmountDue:number)=>{ 
    setTotalPaid(calculatedTotalPaid);
    setAmountDue(calculatedAmountDue);
};

// useEffect(() => {
//     if (trip?.payments && trip.payments.length > 0) {
//       // Calculate total paid amount by summing up all successful payment transactions
//       // Only include transactions with status SUCCESS (matching TripPricingSection logic)
//       let paidAmount = 0;
//       trip.payments.forEach(transaction => {
//         // Only include transactions with status SUCCESS
//         if (transaction.paymentStatus === 'SUCCESS' || transaction.paymentStatus === 'PAID') {
//           paidAmount += transaction.amount;
//         }
//       });
      
//       // Calculate total extra charges
//       const totalExtraCharges = Array.isArray(trip.extraCharges) 
//         ? trip.extraCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0) 
//         : 0;
      
//       // Calculate expected amount (base fare + extra charges)
//       const expectedAmount = (trip.pricing?.pricing?.agreedTripFare || 0) + totalExtraCharges;
      
//       // Calculate amount due
//       const due = Math.max(0, expectedAmount - paidAmount);
      
//       setTotalPaid(paidAmount);
//       setAmountDue(due);
//     } else {
//       // Fallback to pricing data if no payments array
//       const paidAmount = trip?.pricing?.paidAmount || 0;
//       const pendingAmount = trip?.pricing?.pendingAmount || 0;
      
//       setTotalPaid(paidAmount);
//       setAmountDue(pendingAmount);
//     }
//   }, [trip?.payments, trip?.pricing, trip?.extraCharges]);
    // Check for pending payments and refunds
  
    const hasPendingPayments = trip?.payments?.some(
    payment => payment.transactionType === 'PAYMENT' && 
               ['PENDING', 'PROCESSING'].includes(payment.paymentStatus)
  ) || false;
  
  const hasPendingRefunds = trip?.payments?.some(
    payment => payment.transactionType === 'REFUND' && 
               ['PENDING', 'PROCESSING'].includes(payment.paymentStatus)
  ) || false;
  
  // Effect to fetch trip details when tripCode changes
  useEffect(() => {
    if (tripCode && tripCode !== 'new' && tripCode !== 'pricing' && tripCode !== 'callback' && tripCode !== 'contact') {
      fetchTripDetails(tripCode);
    } else if (tripDetail) {
      setTrip(tripDetail);
    } else {
      setTrip(null);
    }
  }, [tripCode, tripDetail]);

  useEffect(() => {
    if (trip?.tripCode != undefined) {
      
      fetchTripDetails(tripCode || '');
    }else{
      
    }
  }, [])

  const handleTripUpdate = async (updatedTrip: TripUpdatePayload) => {
    try {
      if (!tripCode) {
        throw new Error('Trip code is required for update');
      }

      // Use the new API method for trip service details update
      await api.updateTripServiceDetails(tripCode, updatedTrip);

      api.clearCache(`getTripDetails_${tripCode}`);
      await fetchTripDetails(tripCode);

      // Update pricing with the new agreed trip fare from the update payload
      if (trip?.pricing && updatedTrip.pricing && updatedTrip.pricing.agreedTripFare) {
        const updatedPricing = {
          ...trip.pricing,
          pricing: {
            ...trip.pricing.pricing,
            agreedTripFare: updatedTrip.pricing.agreedTripFare,
            totalAmount: updatedTrip.pricing.agreedTripFare
          }
        };
        
        // Update pricing in the local state
        setTrip(prev => prev ? { ...prev, pricing: updatedPricing } : null);
        
        // Optionally update pricing on the server
        try {
          await api.updateTripPricing(tripCode, updatedPricing);
        } catch (pricingError) {
          console.error('Error updating pricing after trip update:', pricingError);
          // Don't throw this error as the main update was successful
        }
      }
    } catch (error: any) {
      console.error('Error updating trip service details:', error);
      throw error; // Re-throw to let the child component handle the error
    }
  };

  const handleFeedbackAdd = async (newFeedback: any) => {
    try {
      if (!tripCode) {
        throw new Error('Trip code is required for update');
      }
      await api.addTripFeedback(tripCode, newFeedback);
      // Update local state immediately, like extra charges
      setTrip(prev => prev ? {
        ...prev,
        feedback: [
          ...(prev.feedback || []),
          {
            ...newFeedback,
            id: Date.now(),
            customerName: prev.customer?.name || 'N/A'
          }
        ]
      } : prev);
      
    } catch (error) {
      console.error('Error adding feedback:', error);
      throw error;
    }
  };

  // const handleCommentsUpdate = async (updatedComments: any[]) => {
  //   try {
  //     if (!tripCode) {
  //       throw new Error('Trip code is required for update');
  //     }
  //     // If adding a new comment, send only the new comment object to backend
  //     if (Array.isArray(updatedComments) && updatedComments.length === 1 && !updatedComments[0].id) {
  //       await api.updateTripComments(tripCode, updatedComments[0]);
  //       // Update local state immediately, like extra charges
  //       setTrip(prev => prev ? {
  //         ...prev,
  //         comments: [
  //           ...(prev.comments || []),
  //           {
  //             ...updatedComments[0],
  //             id: Date.now(),
  //             authorName: updatedComments[0].authorName || 'OPERATOR',
  //             authorType: updatedComments[0].authorType || 'OPERATOR',
  //             commentDate: updatedComments[0].commentDate || new Date().toISOString()
  //           }
  //         ]
  //       } : prev);       
  //     } else {
  //       // For full update, send all comments
  //       await api.updateTripComments(tripCode, updatedComments);
  //       setTrip(prev => prev ? { ...prev, comments: updatedComments } : null);
  //     }
  //   } catch (error: any) {
  //     console.error('Error updating trip comments:', error);
  //     throw error;
  //   }
  // };
  const handleCommentsUpdate = async (newComment: { comment: string }) => {
    try {
      if (!tripCode) {
        throw new Error('Trip code is required for update');
      }
      // Send only the comment object to API
      await api.updateTripComments(tripCode, newComment as any);
      
      // Simply refresh trip details from server
      await fetchTripDetails(tripCode);
        
    } catch (error: any) {
      console.error('Error updating trip comments:', error);
      throw error;
    }
  };
  
  const handlePricingUpdate = async (updatedPricing: any) => {
    try {
      if (!tripCode) {
        throw new Error('Trip code is required for update');
      }

      await api.updateTripPricing(tripCode, updatedPricing);

      // Update local state
      setTrip(prev => prev ? { ...prev, pricing: updatedPricing } : null);
    } catch (error: any) {
      console.error('Error updating trip pricing:', error);
      throw error;
    }
  };

  // const handleExpensesUpdate = async (updatedExpenses: any[]) => {
  //   try {
  //     // Just update local state - API calls are handled by the child component
  //     setTrip(prev => prev ? { ...prev, expenses: updatedExpenses } : null);
  //   } catch (error: any) {
  //     console.error('Error updating trip expenses:', error);
  //     throw error;
  //   }
  // };
  // ...existing code...
  const handleExpensesUpdate = async (updatedExpenses: any[]) => {
    try {
      // Fetch fresh trip data from backend after expense update
      if (tripCode) {
        await fetchTripDetails(tripCode);
      }
    } catch (error: any) {
      console.error('Error updating trip expenses:', error);
      throw error;
    }
  };
// ...existing code...

  // Handler for deleting expense
  const handleDeleteExpense = async (expenseId: number) => {
    try {
      if (!tripCode) throw new Error('Trip code is required');
      const expenseToDelete = trip?.expenses?.find(expense => expense.id === expenseId);
      
      if (!expenseToDelete) {
        throw new Error('Expense not found');
      }     
      await api.deleteTripExpense(tripCode, expenseId);
      
      setTrip(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          expenses: (prev.expenses || []).filter(expense => expense.id !== expenseId)
        };
      });
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  };

  const handleCustomerUpdate = async (updatedCustomer: any) => {
    try {
      if (!trip?.customer?.id) {
        throw new Error('Customer ID is required for update');
      }
      const customerData = {
        name: updatedCustomer.name || '',
        email: updatedCustomer.email || '',
        gender: updatedCustomer.gender || '',
        address: updatedCustomer.address || ''
      };
      await api.updateCustomer(trip.customer.id, customerData);
      setTrip(prev => prev ? {
        ...prev,
        customer: {
          ...prev.customer,
          ...updatedCustomer
        }
      } : null);

    } catch (error: any) {
      console.error('Error updating customer:', error);
      throw error;
    }
  };

  // Add handler for adding extra charge
  const handleAddExtraCharge = async (chargePayload: any) => {
    try {
      if (!tripCode) throw new Error('Trip code is required');
      await api.addTripExtraCharge(tripCode, chargePayload);
      
      // Fetch fresh trip data from backend after adding charge
      await fetchTripDetails(tripCode);
    } catch (error: any) {
      throw error;
    }
  };

  // Handler for deleting extra charge
  const handleDeleteExtraCharge = async (extraChargeId: number) => {
    try {
      if (!tripCode) throw new Error('Trip code is required');
      const chargeToDelete = trip?.extraCharges?.find(charge => charge.id === extraChargeId);
      
      if (!chargeToDelete) {
        throw new Error('Extra charge not found');
      }     
      await api.deleteTripExtraCharge(tripCode, { id: chargeToDelete.id });
      
      // Fetch fresh trip data from backend after deleting charge
      await fetchTripDetails(tripCode);
    } catch (error: any) {
      console.error('Error deleting extra charge:', error);
      throw error;
    }
  };

  // Handler for updating extra charge
  const handleUpdateExtraCharge = async (chargeId: number, chargePayload: any) => {
    try {
      if (!tripCode) throw new Error('Trip code is required');
      await api.updateTripExtraCharge(tripCode, chargeId, chargePayload);
      
      // Fetch fresh trip data from backend after updating charge
      await fetchTripDetails(tripCode);
    } catch (error: any) {
      console.error('Error updating extra charge:', error);
      throw error;
    }
  };

  const handleCancelTrip = async () => {
    try {
      if (!trip?.tripCode) {
        throw new Error('Trip code is required for cancellation');
      } 
      if (!cancelReason) {
        throw new Error('Cancellation reason is required');
      }   
      if (cancelReason === 'Other' && !cancelReasonOther.trim()) {
        throw new Error('Please specify a custom cancellation reason');
      }  
      setCancelling(true);  
      const finalReason = cancelReason === 'Other' ? cancelReasonOther : cancelReason;
      
      const response = await api.cancelTrip(trip.tripCode, {
        notes: finalReason }) as any;
      
      // Reset fields
      setCancelReason('');
      setCancelReasonOther('');
      setCancelNotes('');
      setCancelDialogOpen(false);
      setError(null);  
      // Show success message from response
      const message = response?.data?.message || response?.message || 'Trip cancelled successfully';
      setSuccessMessage(message);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      await fetchTripDetails(trip.tripCode);
      setTrip(prev => prev ? { ...prev, tripStatus: 'CANCELLED' } : null);
      
    } catch (error: any) {
      console.error('Error cancelling trip:', error); 
      // Close dialog and reset form even on error
      setCancelReason('');
      setCancelReasonOther('');
      setCancelNotes('');
      setCancelDialogOpen(false);
      setCancelFormTouched(false);
      
      // Handle specific error cases
      let errorMessage = 'Failed to cancel trip';
      
      if (error.response?.status === 400) {
        // Handle 400 Bad Request errors
        const responseData = error.response?.data;
        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else {
          errorMessage = 'Cannot cancel trip due to validation error';
        }
      } else if (error.response?.data?.message) {
        // Handle other API error responses
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Handle other error types
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  const handleStatusUpdate = async(newStatus: string) => {
    if(newStatus === 'IN CANCELLATION'){
      setInCancellationConfirmation(true);
      setPendingStatusChange(newStatus);
      return;
    }
    // For all other status changes, update immediately
    setTrip(prev => prev ? { ...prev, tripStatus: newStatus } : null);
  };

  const handleCancellationCancel = () => {
    setInCancellationConfirmation(false);
    setPendingStatusChange(null);
  };

  const handleCancellationConfirm = async () => {
    setInCancellationConfirmation(false);
     if(!pendingStatusChange || !trip){
      return;
     }
     try{

      await api.updateTripStatus(trip.tripCode, pendingStatusChange);

      if(trip.driver?.id){
        try{
          await api.removeDriverFromTrip(trip.tripCode);
        }catch(error){
          console.error('Error removing driver:',error);
        }
      }

      if(trip.car?.id){
        try{
          await api.removeCarFromTrip(trip.tripCode);
        }catch(error){
          console.error('Error removing vehicle:', error);
        }
      }
      // Update local state only after successful API call
      setTrip(prev => prev ? { ...prev, tripStatus: pendingStatusChange } : null);
      await fetchTripDetails(trip.tripCode);
      setSuccessMessage('Trip status updated to IN CANCELLATION successfully, Driver and Vehicle unassigned.');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
     }catch(error){
      console.error('Error during cancellation confirmation:', error);
      setError('Failed to update status to IN CANCELLATION. Please try again.');
     }finally{
      setPendingStatusChange(null);
     }
    };

  // Add check for cancelled status
  const isTripCancelled = trip?.tripStatus === 'CANCELLED';
  const isDriverAssigned = trip?.tripStatus === 'DRIVER ASSIGNED';
  const isInCancellation = trip?.tripStatus === 'IN CANCELLATION';
  const isInProgress = trip?.tripStatus === 'IN PROGRESS';
  const isClosed = trip?.tripStatus === 'CLOSED';
  const isJunk = trip?.tripStatus === 'JUNK';
  const isCompleted = trip?.tripStatus === 'COMPLETED';
  const isPending = trip?.tripStatus === 'PENDING';
  const isColdLead = trip?.tripStatus === 'COLD LEAD';
  const isHotLead = trip?.tripStatus === 'HOT LEAD';
  const isAudited = trip?.tripStatus === 'AUDITED';
  const hasFeedback = trip?.feedback && trip.feedback.length > 0;
  // const isTripClosed = trip?.tripStatus === 'CLOSED';

  if (loading) {
    return (
      <DetailContainer>
        <DetailSection>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading trip details...
            </Typography>
          </Box>
        </DetailSection>
      </DetailContainer>
    );
  }

  // Note: Removed early return for error to allow UI sections to remain visible
  if (!trip) {
    return (
      <DetailContainer>
        <DetailSection>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
            <Typography variant="h6" color="textSecondary" textAlign="center" gutterBottom>
              {tripCode ? 'Loading trip details...' : 'No trip selected'}
            </Typography>
            {tripCode && (
              <Typography variant="body2" color="textSecondary" textAlign="center">
                Trip Code: {tripCode}
              </Typography>
            )}
          </Box>
        </DetailSection>
      </DetailContainer>
    );
  }

  // Add this condition before other special cases
  if (tripCode === 'contact') {
    return <ContactRequestPanel />;
  }

  return (
    <DetailContainer>
      {/* Trip Details Header with Cancel Button */}
      {trip && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} p={0.5}>
          <Typography variant="h5" fontWeight="bold">
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh Trip Details">
              <IconButton
                color="primary"
                size="small"
                onClick={() => {
                  if (tripCode) {
                    fetchTripDetails(tripCode);
                  }
                }}
                sx={{backgroundColor: "white"}}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              // disabled={isTripCancelled || !isInCancellation || trip?.payments?.some(payment => payment.paymentStatus === 'SUCCESS')}
              disabled={isTripCancelled || !isInCancellation || totalPaid !== 0}
            >
              Cancel Trip
            </Button>
          </Box>
        </Box>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )
      }
      {error && (
        <Alert severity="error"  sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Trip Status Section - Always enabled */}
      <TripStatusSection
        tripCode={trip?.tripCode || ''}
        currentStatus={trip?.tripStatus || 'UNKNOWN'}
        updatedAt={trip?.updatedAt || ''}
        loading={loading}
        onStatusUpdate={handleStatusUpdate}
        hasDriverAssigned={!!trip?.driver?.id}
        hasVehicleAssigned={!!trip?.car?.id}
        isAdmin={isAdmin}
        isAccountant = {isAccountant}
        totalPaid={totalPaid}
        amountDue={amountDue}
        hasPendingPayments={hasPendingPayments}
        hasPendingRefunds={hasPendingRefunds}
        transactions={trip?.payments || []}
        pickupDatetime={trip?.startDatetime || ''}
        hasFeedback={hasFeedback}
        disabled={isTripCancelled}
        onRefreshTripDetails={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
      />

      {/* Customer Details Section */}
      <CustomerDetailsSection
        customer={trip?.customer || null}
        onUpdate={handleCustomerUpdate}
        loading={loading}
        disabled={isTripCancelled}
      />

      {/* Trip Information Section */}
      <TripInformationSection
        trip={trip}
        onUpdate={handleTripUpdate}
        loading={loading}
        customer={trip?.customer || null}
        isInProgress={isInProgress}
        isCompleted={isCompleted}
        isClosed={isClosed}
        disabled={isTripCancelled || isDriverAssigned || isClosed || isJunk || isDriverAssigned || isInCancellation || isAudited}
      />
      {/* Trip Addresses Section */}
      <TripAddressesSection
        addresses={trip?.addresses || []}
        loading={loading}
        tripCode={trip?.tripCode || ''}
        tripDetail={trip?.travelServiceDetail}
        sourceCity={trip?.sourceCity || ''}
        destinationCity={trip?.destinationCities || ''}
        onUpdate={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        onRefreshTripDetails={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        disabled={isTripCancelled || isDriverAssigned || isInProgress || isClosed || isJunk || isDriverAssigned || isInCancellation || isCompleted || isAudited}
      />

      {/* Trip Pricing Section */}
      <TripPricingSection
        pricing={trip?.pricing || null}
        extraCharges={trip?.extraCharges || []}
        onUpdate={handlePricingUpdate}
        loading={loading}
        tripId={trip?.tripCode || ''}
        isInCancellation={isInCancellation}
        driverDetails={trip?.driver || null}
        refreshTripDetails={async () => {
          if (tripCode) {
            return fetchTripDetails(tripCode);
          }
          return Promise.resolve();
        }}
        customerName={trip?.customer?.name || trip?.passengerName || ''}
        customerMobile={trip?.customer?.mobileNumber || trip?.contactNumber || ''}
        customerEmail={trip?.customer?.email || trip?.email || ''}
        customer={trip?.customer || null}
        disabled={isTripCancelled || isClosed || isJunk || isAudited}
        onPaymentCalculated={handlePaymentCalculated}
      />
      {/* Car Assignment Section */}
      <TripCarSection
        loading={loading}
        tripCode={trip?.tripCode || ''}
        currentCarId={trip?.carId || trip?.car?.id || null}
        carData={trip?.car || null}
        onUpdate={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        disabled={isTripCancelled || isClosed || isJunk || isInCancellation || isCompleted || isAudited}
        currentStatus={trip?.tripStatus}
        hasDriverAssigned={!!trip?.driver?.id}
        isInProgress={isInProgress}
        customerName={trip?.customer?.name}
      />
      {/* Driver Assignment Section */}
      <TripDriverSection
        loading={loading}
        tripCode={trip?.tripCode || ''}
        currentDriverId={trip?.driver?.id || trip?.driverId || null}
        driverData={trip?.driver || null}
        currentStatus={trip?.tripStatus}
        isInProgress={isInProgress}
        onUpdate={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        customerName={trip?.customer?.name || trip?.passengerName || ''}
        customerMobile={trip?.customer?.mobileNumber || trip?.contactNumber || ''}
        customer={trip?.customer || null}
        pickupTime={trip?.startDatetime || ''}
        carType={trip?.car?.carType || ''}
        carNumber={trip?.car?.plateNumber || ''}
        disabled={isTripCancelled || isClosed || isJunk || isInCancellation || isCompleted || isPending || isColdLead || isHotLead || isAudited}
      />

      {/* Trip Extra Charges Section */}
      <TripExtraChargesSection
        extraCharges={trip?.extraCharges || []}
        loading={loading}
        tripCode={trip?.tripCode || ''}
        onAddCharge={handleAddExtraCharge}
        onDeleteCharge={handleDeleteExtraCharge}
        onUpdateCharge={handleUpdateExtraCharge}
        disabled={isTripCancelled || isClosed || isJunk || isInCancellation || isAudited}
      />

      {/* Trip Expenses Section */}
      <TripExpensesSection
        expenses={trip?.expenses || []}
        onUpdate={handleExpensesUpdate}
        onDeleteExpense={handleDeleteExpense}
        loading={loading}
        tripCode={trip?.tripCode || ''}
        disabled={isTripCancelled || isClosed || isJunk || isInCancellation || isAudited}
      />

      {/* Trip Comments Section */}
      <TripCommentsSection
        comments={trip?.comments || []}
        onUpdate={handleCommentsUpdate}
        loading={loading}
        tripCode={trip?.tripCode || ''}
        disabled={isTripCancelled}
      />

      {/* Trip Feedback Section */}
      <TripFeedbackSection
        feedback={trip?.feedback || null}
        onAdd={handleFeedbackAdd}
        onRefresh={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        loading={loading}
        disabled={isTripCancelled || isAudited}
      />
{/* Trip Passengers Section */}
        <TripPassengersSection
        passengers={trip?.passengers || []}
        loading={loading}
        tripCode={trip?.tripCode || ''}
        onUpdate={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        onRefreshTripDetails={async () => {
          if (tripCode) {
            await fetchTripDetails(tripCode);
          }
        }}
        customerName={trip?.customer?.name || trip?.passengerName || ''}
        customerMobile={trip?.customer?.mobileNumber || trip?.contactNumber || ''}
        customerEmail={trip?.customer?.email || trip?.email || ''}
        disabled={isTripCancelled || isDriverAssigned || isClosed || isJunk || isInProgress || isDriverAssigned || isInCancellation || isCompleted || isAudited}
      />
      <Dialog
        open={inCancellationConfirmation}
        onClose={handleCancellationCancel}
        aria-labelledby="cancellation-confirmation-dialog-title"
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            padding: 2
          }
        }}
        >
          {/* <DialogTitle id="cancellation-confirmation-dialog-title">Confirm status change</DialogTitle> */}
          <DialogContent>
            <DialogContentText color="error">
              Driver and Car will be removed from this trip.<br/> Are you sure you want to proceed?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick = {handleCancellationCancel} variant="contained" color="error">
              Cancel
            </Button>
            <Button onClick={handleCancellationConfirm} variant="contained" color="primary" autoFocus>
              Continue
            </Button>
          </DialogActions>   
      </Dialog>
      {/* Cancel Trip Confirmation Dialog */}
      <CancelTripDialog
        open={cancelDialogOpen}
        cancelling={cancelling}
        cancelReason={cancelReason}
        cancelReasonOther={cancelReasonOther}
        cancelNotes={cancelNotes}
        cancelFormTouched={cancelFormTouched}
        cancellationReasons={cancellationReasons}
        onClose={() => {
          setCancelDialogOpen(false);
          setCancelReason('');
          setCancelReasonOther('');
          setCancelNotes('');
          setCancelFormTouched(false);
        }}
        onReasonChange={(reason) => {
          setCancelReason(reason);
          setCancelFormTouched(true);
        }}
        onReasonOtherChange={setCancelReasonOther}
        onNotesChange={setCancelNotes}
        onConfirm={handleCancelTrip}
      />
    </DetailContainer>
  );
};

export default TripDetailPanel;